import { BODYCOMP_INFO_LIST, type BodyCompInfo } from '../const/inbodyData';

export const getBodyCompInfoValueDecimal = (id: string, value: number): number => {
    const decimalPlace = BODYCOMP_INFO_LIST.find(item => item.id === id)?.decimalPlace || 0;

    return Number(value.toFixed(decimalPlace));
};

export const getBodyCompInfoValue = (id: string, infoTypeKey: string) => {
    const info = BODYCOMP_INFO_LIST.find(item => item.id === id);

    if (!info) return '';

    return info[infoTypeKey as keyof BodyCompInfo];
};

/**
 * 16진수 색상을 웹에서 사용할 수 있는 형식으로 변환
 * @param hex 16진수 색상
 * @returns 웹에서 사용할 수 있는 형식의 색상
 */
export const convertHexForWeb = (hex: string): string => {
    // #으로 시작하고 길이가 9 (예: #AARRGGBB)
    if (/^#[0-9a-fA-F]{8}$/.test(hex)) {
        const alpha = hex.slice(1, 3);
        const rgb = hex.slice(3);
        return `#${rgb}${alpha}`;
    }
    // 6자리 hex 또는 그 외 포맷은 그대로 반환
    return hex;
};

export const hexToRgba = (hex: string, alpha: number): string => {
    const [r, g, b] = hex
        .replace(/^#/, '')
        .match(/.{1,2}/g)!
        .map(x => parseInt(x, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * 숫자를 지정된 소수점 자리수 형식으로 포맷팅
 * 정수인 경우 지정된 소수점 자리수를 추가하고, 소수인 경우 지정된 자리수까지 표시
 * @param value 숫자 또는 문자열
 * @param decimalPlaces 표시할 소수점 자리수 (기본값: 1)
 * @returns 지정된 소수점 자리수로 포맷팅된 문자열 (표시용)
 */
export const formatNumberToDecimal = (value: string | number, decimalPlaces: number = 1): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numValue)) {
        return '0';
    }

    // 정수이거나 소수인 경우 모두 지정된 자리수로 포맷팅하여 문자열로 반환
    return numValue.toFixed(decimalPlaces);
};

/**
 * 컨벡스 헐 알고리즘
 * 점들을 포함하는 영역(내부)과 포함하지 않는 영역(외부)을 나누는 경계 역할
 * @param points 점 목록
 * @returns 컨벡스 헐 점 목록
 */
export function computeConvexHull(points: { x: number; y: number }[]): { x: number; y: number }[] {
    if (points.length <= 3) return [...points];

    const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
    const cross = (o: any, a: any, b: any): number => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

    const lower: typeof points = [];
    for (const p of sorted) {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
            lower.pop();
        }
        lower.push(p);
    }

    const upper: typeof points = [];
    for (let i = sorted.length - 1; i >= 0; i--) {
        const p = sorted[i];
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
            upper.pop();
        }
        upper.push(p);
    }

    upper.pop();
    lower.pop();

    return [...lower, ...upper];
}
